import { useEffect, useRef } from 'react';

/**
 * Custom hook to manage browser history for drawers/dialogs
 * When drawer opens, pushes a history state
 * When user presses back button, closes the drawer instead of navigating away
 *
 * @param {boolean} isOpen - Whether the drawer/dialog is currently open
 * @param {function} onClose - Function to call when drawer should close
 * @param {string} id - Unique identifier for this drawer (optional, defaults to 'drawer')
 */
const useDrawerHistory = (isOpen, onClose, id = 'drawer') => {
  const hasHistoryEntry = useRef(false);
  const onCloseRef = useRef(onClose);

  // Keep onClose ref up to date
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    // When drawer opens, push a new history entry
    if (isOpen && !hasHistoryEntry.current) {
      window.history.pushState({ [id]: true }, '');
      hasHistoryEntry.current = true;
    }

    // When drawer closes programmatically (not via back button),
    // remove the history entry if it exists
    if (!isOpen && hasHistoryEntry.current) {
      hasHistoryEntry.current = false;
    }
  }, [isOpen, id]);

  useEffect(() => {
    const handlePopState = (event) => {
      // If our drawer is open and user pressed back, close it
      if (hasHistoryEntry.current) {
        event.preventDefault();
        hasHistoryEntry.current = false;
        onCloseRef.current();
      }
    };

    // Only add listener when drawer is open
    if (isOpen) {
      window.addEventListener('popstate', handlePopState);
    }

    // Cleanup: remove listener and clean up history if needed
    return () => {
      window.removeEventListener('popstate', handlePopState);

      // If drawer is still open when unmounting and we have a history entry,
      // go back to clean up the history stack
      if (isOpen && hasHistoryEntry.current) {
        hasHistoryEntry.current = false;
        // Use try-catch in case history state is invalid
        try {
          if (window.history.state?.[id]) {
            window.history.back();
          }
        } catch (e) {
          // Ignore errors
        }
      }
    };
  }, [isOpen, id]);
};

export default useDrawerHistory;
